<template>
  <TheMainContent class="contents">
    <FailLoadingScreen
      v-if="displayFailLoadingScreen"
      data-qa="diagnosis-fail-loading-screen"
      data-test="diagnosis-fail-loading-screen"
      @retry="onRetry"
    />

    <InvalidProblemErrorScreen
      v-if="displayInvalidLoadingProblemContextErrorScreen"
    />

    <LoadingScreen
      v-if="displayLoadingScreen"
      data-qa="diagnosis-loading-screen"
      data-test="diagnosis-loading-screen"
      :loading-text="i18n(translations.loadingText)"
    />

    <PickIssuesScreen
      v-if="displayPickIssuesScreen"
      data-qa="diagnosis-pick-issues-screen"
      data-test="diagnosis-pick-issues-screen"
      :machine-context="state.context"
      :machine-send="send"
      @cancel="onCancel"
    />

    <CancellationModal />
    <ImeiModal
      :imei
      @close="onClosedModal"
      @still-need-help="onIMEIstillNeedHelp"
    />

    <ImeiUnavailableModal
      :first-name="user.firstName"
      @close="onClosedModal"
      @still-need-help="onIMEIstillNeedHelp"
    />

    <InsurerPortalModal :insurer-portal-url @close="onClosedModal" />

    <InvoiceRequestCanceledModal @close="onClosedModal" />

    <InvoiceRequestModal @close="onClosedModal" />

    <InvoiceRequestPendingModal
      @close="onClosedModal"
      @send-another-invoice-request="onSendAnotherInvoiceRequest"
    />

    <MobileServiceRedirectModal
      v-if="
        orderlineId &&
        state.context.mobileServicePartnerProviderLabel &&
        state.context.mobileServicePartnerProviderUrl
      "
      :mobile-service-name="state.context.mobileServicePartnerProviderLabel"
      :orderline-id
      :redirection-url="state.context.mobileServicePartnerProviderUrl"
      @close="onClosedModal"
    />

    <PreShippingInvoiceRequestModal @close="onClosedModal" />
  </TheMainContent>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onMounted, onUnmounted, shallowRef, watch } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { openModal } from '@ds/components/ModalBase'
import {
  type ActorRef,
  type State,
  type StateSchema,
  type Typestate,
  interpret,
} from 'xstate'

import { useOrderlineLightDetails } from '@/scopes/care-commons/components/order/OrderlineCard/composables/useOrderlineLightDetails'
import MobileServiceRedirectModal from '@/scopes/care-commons/modals/MobileServiceRedirectModal/MobileServiceRedirectModal.vue'
import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import { SALES_CUSTOMER_CARE_SCOPE } from '~/scopes/care-commons/utils/logger/salesCustomerCare/config'

import FailLoadingScreen from '../common/components/FailLoadingScreen/FailLoadingScreen.vue'
import InvalidProblemErrorScreen from '../common/components/InvalidProblemErrorScreen/InvalidProblemErrorScreen.vue'
import LoadingScreen from '../common/components/LoadingScreen/LoadingScreen.vue'
import type {
  ToastMachineContext,
  ToastMachineEvent,
} from '../common/machine/Toast.machine.types'
import CancellationModal from '../common/modals/CancellationModal/CancellationModal.vue'
import { CUSTOMER_CARE_MODALS_NAMES } from '../common/modals/names'

import translations from './Diagnosis.translations'
import PickIssuesScreen from './components/PickIssuesScreen/PickIssuesScreen.vue'
import { createDiagnosisMachine } from './machine/Diagnosis.machine'
import { actors, toasts } from './machine/Diagnosis.machine.constants'
import { getExternalUrlFromSelectedIssues } from './machine/selectors/getExternalUrl'
import { getIMEI } from './machine/selectors/getIMEI'
import ImeiModal from './modals/ImeiModal/ImeiModal.vue'
import ImeiUnavailableModal from './modals/ImeiUnavailableModal/ImeiUnavailableModal.vue'
import InsurerPortalModal from './modals/InsurerPortalModal/InsurerPortalModal.vue'
import InvoiceRequestCanceledModal from './modals/InvoiceRequestCanceledModal/InvoiceRequestCanceledModal.vue'
import InvoiceRequestModal from './modals/InvoiceRequestModal/InvoiceRequestModal.vue'
import InvoiceRequestPendingModal from './modals/InvoiceRequestPendingModal/InvoiceRequestPendingModal.vue'
import PreShippingInvoiceRequestModal from './modals/PreShippingInvoiceRequestModal/PreShippingInvoiceRequestModal.vue'

const i18n = useI18n()
const { params } = useRoute()
const { user } = useUserStore()
const { openErrorToast } = useTheToast()

const {
  data: orderline,
  status,
  execute,
} = useOrderlineLightDetails(
  params.orderlineId as string,
  SALES_CUSTOMER_CARE_SCOPE.diagnosis,
)

const orderlineIdIsUUID = Number.isNaN(Number(params.orderlineId))

if (orderlineIdIsUUID) {
  await execute()
}

const orderlineId = computed<number | null>(() => {
  if (orderlineIdIsUUID) {
    return orderline.value?.orderlineId ?? null
  }

  return Number(params.orderlineId)
})

const diagnosisMachine = createDiagnosisMachine('LOADING')

const state = shallowRef(diagnosisMachine.initialState)

const service = interpret(
  diagnosisMachine.withContext({
    ...diagnosisMachine.context,
    orderId: orderline.value?.orderId ?? null,
    orderlineId: orderlineId.value,
    mobileServicePartnerProviderLabel:
      orderline.value?.bundledMobilePlan?.partnerProviderLabel ?? null,
    mobileServicePartnerProviderUrl:
      orderline.value?.bundledMobilePlan?.partnerPortalUrl ?? null,
  }),
  {
    devTools: false,
  },
).onTransition((newState) => {
  state.value = newState
})
const { send } = service
onMounted(() => {
  service.start()
})

watch(
  () => status.value,
  (currentStatus) => {
    if (currentStatus === 'success' && !orderlineIdIsUUID) {
      send({
        type: 'UPDATE_ORDERLINE_CONTEXT',
        payload: {
          orderId: orderline.value?.orderId ?? null,
          mobileServicePartnerProviderLabel:
            orderline.value?.bundledMobilePlan?.partnerProviderLabel ?? null,
          mobileServicePartnerProviderUrl:
            orderline.value?.bundledMobilePlan?.partnerPortalUrl ?? null,
        },
      })
    }
  },
)

onUnmounted(() => {
  service.stop()
})

const displayFailLoadingScreen = computed(() =>
  state.value.hasTag('loading_failed'),
)

const displayLoadingScreen = computed(() => state.value.hasTag('loading'))

const displayInvalidLoadingProblemContextErrorScreen = computed(() =>
  state.value.hasTag('loading_problem_context_failed'),
)

const imei = computed(() => getIMEI(state.value.context.issueAction))

const displayPickIssuesScreen = computed(() =>
  state.value.matches('ISSUE_SELECTION'),
)

const insurerPortalUrl = computed(() =>
  getExternalUrlFromSelectedIssues(state.value.context.pageData),
)

const toastSnapshot = computed<State<
  ToastMachineContext,
  ToastMachineEvent,
  StateSchema<ToastMachineContext>,
  Typestate<ToastMachineContext>
> | null>(() => {
  const toastActor = state.value.children[actors.Toast]

  if (toastActor) {
    const toastActorRef = toastActor as ActorRef<ToastMachineEvent>

    return toastActorRef.getSnapshot()
  }

  return null
})

const openRequestFailedToast = computed(
  () =>
    toastSnapshot.value?.context.activeToast === toasts.RequestFailed &&
    toastSnapshot.value?.matches('open'),
)

watch(
  openRequestFailedToast,
  (shouldOpen) => {
    if (shouldOpen) {
      openErrorToast({
        title: i18n(translations.toastTitle),
        content: i18n(translations.toastContent),
        name: 'requestFailed',
        closeCallback: () => send('TOAST_CLOSED'),
      })
    }
  },
  { immediate: true },
)

function onCancel() {
  openModal(CUSTOMER_CARE_MODALS_NAMES.cancellationModal)
}

function onClosedModal() {
  send('MODAL_CLOSED')
}

function onIMEIstillNeedHelp() {
  send('CONTINUE_WITH_SELECTED_ISSUES')
}

function onRetry() {
  send('RETRY')
}

function onSendAnotherInvoiceRequest() {
  send('SEND_ANOTHER_INVOICE_REQUEST')
}
</script>
